import {IComponent} from '@assets/js/@types/Component';

interface NavigationComponent extends IComponent {
  setElements(mainEl: HTMLElement): void;

  openNavigation(): void;

  closeNavigation(): void;

  toggleNavigation(): void;

  initEventListener(): void;

  setTabGlider(tabButtons: NodeListOf<Element>, glider: Element): void;

  getWindowSize(): number;

  initMobileMenu(): void;

  resetMobileMenu(): void;

  animationLogic(): void;
}

const navigation: NavigationComponent = {
  cfg: {
    name: 'c-navigation',
    selectors: {
      navigation: '.js-navigation',
      backdrop: '.backdrop',
      backdropMobile: '.backdrop-mobile',
      hamburger: '.js-hamburger',
      tabs: '.js-tabs',
      goBackButtons: '.goBack',
      navItem: '.nav-item-anchor.has-childs',
      item: '.nav-item.has-childs',
      tabButtons: '.tab-button',
      dropDownWrapper: '.dropdown-wrapper',
      placeholder: 'placeholder',
    },
    classes: {
      active: 'is-active',
      hidden: 'hidden',
      overflowHidden: 'overflow-hidden',
      mobileMenuInit: 'mobile-menu-initialized',
      dropDownMoveIn: 'move-in',
    },
    vars: {
      offsetVal: 0,
      lastScrollTop: 0,
      currentScrollTop: 0,
      hasScrolledUp: null,
      scrolled72px: null,
      hasStartedScrollingUp: null,
      startScrollUpPosition: 0,
      navScrollPos: 0,
      navScrollPosAfterScrollUp: 0,
      wasAtTop: null,
      menuOpen: null,
    },
    el: {
      $navigation: null,
      $backdrop: null,
      $hamburger: null,
      $tabs: null,
      $goBackButtons: null,
      $navItems: null,
      $items: null,
      $tabButtons: null,
      $placeholder: null,
      $dropDownWrapper: null,
    },
  },
  setElements(mainEl: HTMLElement) {
    this.cfg.el.$navigation = mainEl;
    this.cfg.el.$backdrop = document.querySelector(this.cfg.selectors.backdrop);
    this.cfg.el.$hamburger = document.querySelector(this.cfg.selectors.hamburger);
    this.cfg.el.$tabs = document.querySelectorAll(this.cfg.selectors.tabs);
    this.cfg.el.$goBackButtons = document.querySelectorAll(this.cfg.selectors.goBackButtons);
    this.cfg.el.$navItems = document.querySelectorAll(this.cfg.selectors.navItem);
    this.cfg.el.$items = document.querySelectorAll(this.cfg.selectors.item);
    this.cfg.el.$tabButtons = document.querySelectorAll(this.cfg.selectors.tabButtons);
    this.cfg.el.$windowSizeMax = 1023;
    this.cfg.el.$placeholder = document.getElementById(this.cfg.selectors.placeholder);
    this.cfg.vars.hasStartedScrollingUp = false;
    this.cfg.vars.wasAtTop = true;
    this.cfg.vars.menuOpen = false;

    if (window.innerWidth < 1024) {
      this.cfg.vars.offsetVal = 52;
    } else {
      this.cfg.vars.offsetVal = 72;
    }
  },

  getWindowSize() {
    return window.innerWidth;
  },

  openNavigation() {
    this.cfg.el.$hamburger.setAttribute('aria-expanded', 'true');
    this.cfg.el.$navigation.classList.add(this.cfg.classes.active);

    const mainBackDropMobile = document.querySelector(this.cfg.selectors.backdropMobile);
    mainBackDropMobile.classList.add(this.cfg.classes.active);

    document.body.classList.add(this.cfg.classes.overflowHidden);
  },

  closeNavigation() {
    this.cfg.el.$hamburger.setAttribute('aria-expanded', 'false');
    this.cfg.el.$navigation.classList.remove(this.cfg.classes.active);

    const mainBackDropMobile = document.querySelector(this.cfg.selectors.backdropMobile);
    mainBackDropMobile.classList.remove(this.cfg.classes.active);

    document.body.classList.remove(this.cfg.classes.overflowHidden);

    this.resetMobileMenu();
  },

  resetMobileMenu() {
    const elements = this.cfg.el.$navigation.querySelectorAll(`.${this.cfg.classes.dropDownMoveIn}`);
    elements.forEach((el: HTMLElement) => {
      el.classList.remove(this.cfg.classes.dropDownMoveIn);
    });

    this.cfg.el.$navigation.classList.remove(this.cfg.classes.mobileMenuInit);
  },

  toggleNavigation() {
    const menuOpen = this.cfg.el.$navigation.classList.contains(this.cfg.classes.active);

    if (menuOpen) {
      this.closeNavigation();
    } else {
      this.openNavigation();
    }
  },

  setTabGlider(tabButtons: NodeListOf<Element>, glider: Element) {
    if (!glider) {
      return;
    }

    Array.from(tabButtons).find((tab: HTMLElement) => {
      if (tab.classList.contains(this.cfg.classes.active)) {
        const {offsetLeft} = tab;
        const width = tab.clientWidth + 2;

        glider.setAttribute('style', `width: ${width}px; transform: translateX(${offsetLeft}px)`);

        return true;
      }

      return false;
    });
  },

  initMobileMenu() {
    this.cfg.el.$goBackButtons.forEach((button: HTMLElement) => {
      button.addEventListener('click', (event) => {
        const {target} = event;
        console.log(target instanceof Element);
        if (!target || !(target instanceof Element)) {
          return;
        }

        const currentTabContent = target.closest(`.${this.cfg.classes.dropDownMoveIn}`);

        if (!currentTabContent) {
          return;
        }

        const currentDropDown = currentTabContent.closest(this.cfg.selectors.dropDownWrapper);
        const tabs = currentDropDown.querySelectorAll(this.cfg.selectors.tabButtons);
        const goBackButtons = currentDropDown.querySelectorAll(this.cfg.selectors.goBackButtons);

        // If only one tab exist skipp this level mobile
        if (tabs.length === 1) {
          goBackButtons[0].classList.add('type-alone');
          goBackButtons[0].click();
        }

        currentTabContent?.classList.remove(this.cfg.classes.dropDownMoveIn);
      });
    });

    this.cfg.el.$navItems.forEach((navItem: HTMLElement) => {
      navItem.addEventListener('click', (event) => {
        const {target} = event;
        if (!target || !(target instanceof Element)) {
          return;
        }

        const currentTarget = target.closest(this.cfg.selectors.navItem);
        const currentDropDown = currentTarget.nextElementSibling;

        if (currentDropDown && this.getWindowSize() <= this.cfg.el.$windowSizeMax) {
          event.preventDefault();
          const tabs = currentDropDown.querySelectorAll(this.cfg.selectors.tabButtons);

          const backdropMobile = currentTarget.closest('#primary-navigation').previousElementSibling;

          backdropMobile?.classList.add(this.cfg.classes.active);
          currentDropDown.classList.add(this.cfg.classes.dropDownMoveIn);
          setTimeout(() => {
            backdropMobile?.classList.remove(this.cfg.classes.active);
          }, 500);

          // If only one tab exist skipp this level mobile
          if (tabs.length === 1) {
            const goBackButton = currentDropDown.querySelectorAll(this.cfg.selectors.goBackButtons);

            goBackButton[0].classList.add('type-alone');
            tabs[0].click();
          }
        }
      });
    });

    this.cfg.el.$tabButtons.forEach((tab: HTMLElement) => {
      tab.addEventListener('click', (event) => {
        const {target} = event;
        if (!target || !(target instanceof Element)) {
          return;
        }

        const currentTarget = target.closest('.tab-button');

        if (!currentTarget) {
          return;
        }
        const id = currentTarget.getAttribute('data-id');

        if (id && this.getWindowSize() <= this.cfg.el.$windowSizeMax) {
          const dropdownWrapper = currentTarget.closest(this.cfg.selectors.dropDownWrapper);

          const backdropMobile = dropdownWrapper.querySelector(this.cfg.selectors.backdropMobile);
          backdropMobile?.classList.add(this.cfg.classes.active);
          setTimeout(() => {
            backdropMobile?.classList.remove(this.cfg.classes.active);
          }, 500);

          const element = document.getElementById(id);
          element?.classList.add(this.cfg.classes.dropDownMoveIn);
        }
      });
    });
  },

  initEventListener() {
    this.cfg.el.$hamburger.addEventListener('click', this.toggleNavigation.bind(this));

    this.cfg.el.$navItems.forEach((item: HTMLElement) => {
      const parentEl = item.parentNode as HTMLElement;

      item.addEventListener('click', (event: MouseEvent) => {
        event.stopPropagation();

        if (!parentEl.classList.contains('opened')) {
          this.cfg.el.$navItems.forEach((link: HTMLElement) => {
            const allParentEl = link.parentNode as HTMLElement;
            if (allParentEl.classList.contains('opened')) {
              this.cfg.el.$dropDownWrapper.classList.remove('dropdown-open');
              allParentEl.classList.remove('opened');
            }
          });
        }

        this.cfg.el.$dropDownWrapper = item.parentNode?.querySelector(this.cfg.selectors.dropDownWrapper);
        this.cfg.el.$backdrop.classList.add(this.cfg.classes.active);

        if (!parentEl.classList.contains('opened')) {
          this.cfg.el.$dropDownWrapper.classList.add('dropdown-open');
          parentEl.classList.add('opened');
        } else {
          this.cfg.el.$dropDownWrapper.classList.remove('dropdown-open');
          parentEl.classList.remove('opened');
          this.cfg.el.$backdrop.classList.remove(this.cfg.classes.active);
        }
      });
    });

    this.cfg.el.$backdrop.addEventListener('click', () => {
      this.cfg.el.$navItems.forEach((link: HTMLElement) => {
        const allParentEl = link.parentNode as HTMLElement;
        if (allParentEl.classList.contains('opened')) {
          this.cfg.el.$dropDownWrapper.classList.remove('dropdown-open');
          allParentEl.classList.remove('opened');
        }
      });
      this.cfg.el.$backdrop.classList.remove(this.cfg.classes.active);
    });

    window.addEventListener('resize', () => {
      if (this.getWindowSize() > this.cfg.el.$windowSizeMax) {
        this.closeNavigation();

        if (this.cfg.el.$backdrop.classList.contains(this.cfg.classes.active)) {
          this.cfg.el.$backdrop.classList.remove(this.cfg.classes.active);
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeNavigation();
      }
    });

    this.cfg.el.$tabs.forEach((tab: Element) => {
      const tabButtons = tab.querySelectorAll('.tab-button');
      const tabGlider = tab.querySelector('.glider');
      const contents = tab.querySelectorAll('.tab-content');
      this.setTabGlider(tabButtons, tabGlider);
      window.addEventListener('resize', () => {
        this.setTabGlider(tabButtons, tabGlider);
      });

      tab.addEventListener('click', (event) => {
        const {target} = event;

        if (!target || !(target instanceof Element)) {
          return;
        }

        const currentTarget = target.closest('.tab-button');

        if (!currentTarget) {
          return;
        }

        const id = currentTarget.getAttribute('data-id');
        if (id) {
          tabButtons.forEach((btn) => {
            btn.classList.remove(this.cfg.classes.active);
          });
          currentTarget.classList.add('is-active');
          this.setTabGlider(tabButtons, tabGlider);

          contents.forEach((content) => {
            content.classList.remove(this.cfg.classes.active);
          });
          const element = document.getElementById(id);
          element?.classList.add(this.cfg.classes.active);
        }
      });
    });
  },

  animationLogic() {
    // eslint-disable-next-line no-restricted-globals
    history.scrollRestoration = 'manual';
    window.addEventListener('scroll', () => {
      this.cfg.vars.currentScrollTop = document.documentElement.scrollTop;
      this.cfg.el.$navigation.classList.add('-translate-x-2/4');
      this.cfg.el.$navigation.classList.add('left-1/2');

      if (this.cfg.vars.currentScrollTop > this.cfg.vars.lastScrollTop) {
        if (window.innerWidth > 768) {
          this.cfg.el.$placeholder.classList.remove('hidden');
        } else {
          this.cfg.el.$placeholder.classList.add('hidden');
        }
        // Zurücksetzen, da der Benutzer nach unten scrollt
        this.cfg.vars.hasStartedScrollingUp = false;
        this.cfg.vars.navScrollPos = this.cfg.vars.lastScrollTop;

        if (this.cfg.vars.currentScrollTop >= this.cfg.vars.navScrollPosAfterScrollUp) {
          this.cfg.el.$navigation.classList.remove('md:fixed');
          this.cfg.el.$navigation.classList.add('md:absolute');
          this.cfg.el.$navigation.classList.remove('!top-0');
          if (!this.cfg.vars.wasAtTop) {
            if (window.innerWidth > 768) {
              this.cfg.el.$navigation.style.top = `${this.cfg.vars.navScrollPosAfterScrollUp}px`;
            }
          }
        }
      }
      // Prüfen, ob der Benutzer nach oben scrollt
      else if (this.cfg.vars.currentScrollTop < this.cfg.vars.lastScrollTop) {
        if (parseInt(window.getComputedStyle(this.cfg.el.$navigation).top, 10) < 0) {
          this.cfg.vars.navScrollPos = 0;
          this.cfg.vars.navScrollPosAfterScrollUp = 0;
          this.cfg.el.$navigation.classList.add('!top-0');
        }
        // Startposition für das Scrollen nach oben festlegen
        if (!this.cfg.vars.hasStartedScrollingUp) {
          this.cfg.vars.startScrollUpPosition = this.cfg.vars.lastScrollTop;
          this.cfg.vars.hasStartedScrollingUp = true;
        } else if (this.cfg.vars.currentScrollTop === 0) {
          this.cfg.el.$navigation.classList.add('md:absolute');
          this.cfg.el.$navigation.classList.add('!top-0');
          this.cfg.el.$navigation.style.top = 0;
          this.cfg.vars.navScrollPos = 0;
          this.cfg.vars.navScrollPosAfterScrollUp = 0;
        }
        // Prüfen, ob 72px nach oben gescrollt wurde
        else if (this.cfg.vars.startScrollUpPosition - this.cfg.vars.currentScrollTop >= this.cfg.vars.offsetVal) {
          this.cfg.el.$navigation.classList.remove('md:absolute');
          this.cfg.el.$navigation.classList.add('md:fixed');
          this.cfg.el.$navigation.classList.add('!top-0');
          this.cfg.vars.navScrollPosAfterScrollUp = this.cfg.vars.currentScrollTop + 100;
          // Zurücksetzen für die nächste Überprüfung
        } else {
          this.cfg.vars.wasAtTop = false;
          if (window.innerWidth > 768) {
            this.cfg.el.$navigation.style.top = `${this.cfg.vars.navScrollPos - this.cfg.vars.offsetVal}px`;
          }
        }
      }
      this.cfg.vars.lastScrollTop = this.cfg.vars.currentScrollTop;
    });
  },

  init(el: HTMLElement) {
    this.setElements?.(el);
    this.initEventListener();
    this.initMobileMenu();
    this.animationLogic();
  },
};

export default navigation;
